import { Group, Panel, Separator } from 'react-resizable-panels';

import { cn } from '@/lib/utils';

const ResizablePanelGroup = ({ className, ...props }: React.ComponentProps<typeof Group>) => (
  <Group
    className={cn('flex h-full w-full data-[panel-group-direction=vertical]:flex-col', className)}
    {...props}
  />
);

const ResizablePanel = Panel;

/**
 * Mora-style resize handle. 1px line by default, reveals a small editorial
 * grip pill on hover/drag. Matches the home-page split-view aesthetic.
 *
 * The hidden grip uses an `::after` pseudo-element on the Separator and
 * fades in via opacity transition only — no layout shift, no JS, pure CSS.
 */
const ResizableHandle = ({
  withHandle,
  disabled,
  seamless,
  className,
  ...props
}: React.ComponentProps<typeof Separator> & {
  withHandle?: boolean;
  /**
   * Keep the resize target interactive without painting a divider or grip.
   * Used by inset companion panels where the canvas gap and elevated surface
   * already communicate the boundary.
   */
  seamless?: boolean;
  /**
   * Static seam: render the 1px line but no resize affordance. Used for the
   * fixed (min===max) project-hub pane, where the handle is purely the visual
   * divider between the rail and the main column — a resize cursor + grip on an
   * immovable divider would be dishonest. The seam is owned here in ONE place
   * for every right-panel mode (FloatingPanel no longer draws its own border).
   */
  disabled?: boolean;
}) => (
  <Separator
    disabled={disabled}
    className={cn(
      'group/handle relative w-px shrink-0 transition-colors duration-150',
      seamless ? 'bg-transparent' : 'bg-border/40',
      // Interactive affordances only when the handle can actually move.
      !disabled && !seamless && 'hover:bg-border/80',
      !disabled &&
        !seamless &&
        'data-[resize-handle-state=hover]:bg-border/80 data-[resize-handle-state=drag]:bg-foreground/40',
      'data-[panel-group-direction=vertical]:h-px data-[panel-group-direction=vertical]:w-full',
      className,
    )}
    {...props}
  >
    {/* Grip pill. Hidden until hover by default; `withHandle` gives global
        drawers a quiet, always-present resize cue without adding header chrome.

        🔴 `seamless` NO LONGER SUPPRESSES THE GRIP — it only drops the LINE.
        The two were conflated, so the inset floating-card panels (agent, idea,
        ad) had no visible resize affordance at all: `seamless` removed the 1px
        seam, which is correct because the card draws its own border, and took
        the grab handle with it, which was not. Founder, 2026-08-17, on the ad
        panel: _"there should be the draggable handle on that panel, but there
        shouldn't be the divider line — that draggable handle should be there,
        but the full top-to-bottom line shouldn't be."_

        A caller that wants neither passes neither `withHandle` nor a non-seamless
        seam, so nothing gains chrome it did not ask for; the three global-dock
        panels pass `withHandle` and now get exactly what it says. */}
    {!disabled && (!seamless || withHandle) && (
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
          'flex h-10 w-1 items-center justify-center rounded-full',
          'transition duration-200 ease-out',
          withHandle ? 'bg-border/60 opacity-60' : 'bg-border/0 opacity-0',
          'group-hover/handle:w-1 group-hover/handle:bg-border group-hover/handle:opacity-100',
          'group-data-[resize-handle-state=hover]/handle:bg-border',
          'group-data-[resize-handle-state=hover]/handle:opacity-100',
          'group-data-[resize-handle-state=drag]/handle:bg-foreground/60',
          'group-data-[resize-handle-state=drag]/handle:opacity-100',
        )}
      />
    )}
  </Separator>
);

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
